<template>
  <div id="pulseReport">
    <div class="wrapper">
      <el-card
        v-loading="loading"
        class="reportCard"
        shadow="always"
        :body-style="{padding: '70px'}"
      >
        <div
          class="title"
          :class="`pulse${pulseType}`"
        >
          Pulse Report {{ pulseType === 1 ? 'I' : 'II' }}
        </div>

        <div class="content">
          <div class="page">
            <div class="subTitle">
              Question 1
            </div>
            <div class="question">
              Over the last approx. {{ pulseType === 1 ? '3' : '6' }} months,
              how effective do you believe {{ leader.name }} has become in
              demonstrating the following leadership behaviors? Do not consider
              environmental factors beyond their control.
            </div>

            <div class="question QTitle">
              A. {{ leader.lga.lga1 }}
            </div>
            <div class="response">
              <div class="graphHelper">
                <div class="ninHelper">
                  NIN = No Improvement Needed
                </div>

                <div class="helpOne">
                  Pulse I
                </div>
                <div class="helpTwo">
                  Pulse II
                </div>
              </div>

              <div class="chartWrapper">
                <div class="axisTitle effect">
                  <div
                    v-for="(item, Effect1Key) in axisTitle.effect"
                    :key="`Axis${Effect1Key}`"
                  >
                    {{ item }}
                  </div>
                </div>
                <PulseQ1Chart
                  v-if="report"
                  :role="role"
                  :report="report"
                  :scene="'question1a'"
                />
              </div>
            </div>

            <div class="question QTitle">
              B. {{ leader.lga.lga2 }}
            </div>
            <div class="response">
              <div class="chartWrapper">
                <div class="axisTitle effect">
                  <div
                    v-for="(item, Effect2Key) in axisTitle.effect"
                    :key="`Axis${Effect2Key}`"
                  >
                    {{ item }}
                  </div>
                </div>
                <PulseQ1Chart
                  v-if="report"
                  :role="role"
                  :report="report"
                  :scene="'question1b'"
                />
              </div>
            </div>
          </div>

          <div class="page">
            <div class="subTitle">
              Question 2
            </div>
            <div class="question">
              Did {{ leader.name }} implement their action plan and make
              change visible to you over the last {{ pulseType === 1 ? '3' : '6' }} months?
            </div>

            <div class="response">
              <div class="graphHelper">
                <div class="helpOne">
                  Pulse I
                </div>
                <div class="helpTwo">
                  Pulse II
                </div>
              </div>

              <div class="chartWrapper">
                <div class="axisTitle visible">
                  <div
                    v-for="(item, AxisKey) in axisTitle.visible"
                    :key="`Axis${AxisKey}`"
                  >
                    {{ item }}
                  </div>
                </div>
                <PulseQ1Chart
                  v-if="report"
                  :role="role"
                  :report="report"
                  :scene="'question2'"
                />
              </div>
            </div>
          </div>

          <div class="page">
            <div class="subTitle">
              Question 3
            </div>
            <div class="question">
              What has {{ leader.name }} done in their leadership growth areas
              over the last approx. {{ pulseType === 1 ? '3' : '6' }} months
              to become a more effective leader and what did you find particularly effective?
              You can add comments in your preferred language:
            </div>

            <div class="response">
              <VerbatimTable
                v-if="report"
                :report="report"
                :scene="'question3'"
                :pulse-type="pulseType"
              />
            </div>
          </div>

          <div class="page">
            <div class="subTitle">
              Question 4
            </div>
            <div class="question">
              What can {{ leader.name }} do in their leadership growth areas
              to become more effective as a leader in the next 3-4 months?
              You can add comments in your preferred language:
            </div>

            <div class="response">
              <VerbatimTable
                v-if="report"
                :report="report"
                :scene="'question4'"
                :pulse-type="pulseType"
              />
            </div>
          </div>

          <div class="page">
            <div class="subTitle">
              Question 5
            </div>
            <div class="question">
              Are there any additional comments you would like to share with {{ leader.name }}?
              You can add comments in your preferred language:
            </div>

            <div class="response">
              <VerbatimTable
                v-if="report"
                :report="report"
                :scene="'question5'"
                :pulse-type="pulseType"
              />
            </div>
          </div>

          <!--<el-pagination
            layout="prev, pager, next"
            :page-count="maxPage"
            :current-page.sync="currentPage"
          />-->
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import dialog from '@/mixins/dialog';
import api from '@/api';

export default {
  components: {
    PulseQ1Chart: () => import(/* webpackChunkName: "Charts" */ './charts/PulseQ1Chart.vue'),
    VerbatimTable: () => import(/* webpackChunkName: "Charts" */ './charts/VerbatimTable.vue'),
  },

  mixins: [dialog],

  data() {
    return {
      loading: false,
      role: '',
      pulseType: 1,
      currentPage: 1,
      maxPage: 5,
      leader: {
        lga: {
          lga1: '',
          lga2: '',
        },
        name: '',
      },
      axisTitle: {
        effect: [
          'Much more effective',
          'More effective',
          'Slightly more effective',
          'No change',
          'Slightly less effective',
          'Less effective',
          'Much less effective',
        ],
        visible: [
          'Very Frequently',
          'Frequently',
          'Occasionally',
          'Rarely',
          'Very Rarely',
          'Never',
        ],
      },
      report: null,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user.user,
    }),
  },

  created() {
    if (this.$route.params.id) {
      this.role = 'coach';
    } else {
      this.role = 'leader';
    }

    this.getLeader();
    this.getReport();
  },

  methods: {
    async getLeader() {
      try {
        let response = null;
        const structure = {};

        if (this.role === 'leader') {
          response = await api.getLGA();
          structure.lga = response.data;
          structure.name = `${this.user.first_name} ${this.user.last_name}`;
        } else if (this.user.role_type === 'coach') {
          response = await api.getCoachLeaderDetails(this.$route.params.id);
          structure.lga = response.data.lga;
          structure.name = response.data.name;
        } else if (this.user.role_type === 'OA') {
          response = await api.getLeaderDetails(this.$route.params.id);
          structure.lga = { lga1: response.data.lga1, lga2: response.data.lga2 };
          structure.name = response.data.name;
        }

        this.leader = structure;
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
        console.log(err);
      }
    },

    async getReport() {
      try {
        this.loading = true;
        let response = null;

        if (this.role === 'leader') {
          response = await api.getPulseReportLeader();
        } else {
          let params;
          if (['MAM', 'coach'].includes(this.user.role_type) && this.$route.query.role === 'coach') {
            params = { bypass: true };
          }
          response = await api.getLeaderPulse(this.$route.params.id, params);
        }
        this.report = response.data;
        this.setPulseName();
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
        console.log(err);
      } finally {
        this.loading = false;
      }
    },

    setPulseName() {
      if (this.report.pulse1) {
        this.pulseType = 1;
      }

      // Pulse 2 data
      if (this.report.pulse2) {
        this.pulseType = 2;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #pulseReport {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 682px;
    min-height: 100vh;
    background: #808080;

    .wrapper {
      margin: 50px auto;

      .reportCard {
        border-radius: 0;
        margin: 0 auto;
        width: 680px;

        .title {
          font-weight: bold;
          font-size: 16px;
          padding-bottom: 5px;
          margin-bottom: 30px;

          &.pulse1 {
            color: #00ace2;
            border-bottom: 2px solid #00ace2;
          }

          &.pulse2 {
            color: #02be90;
            border-bottom: 2px solid #02be90;
          }
        }

        .content {
          .page {
            margin-bottom: 20px;
          }

          .subTitle {
            color: rgba(0, 0, 0, 0.5);
            margin-bottom: 15px;
          }

          .question {
            padding: 10px 0;

            &.QTitle {
              padding: 20px 0 10px;
            }
          }

          .response {
            .graphHelper {
              display: flex;
              justify-content: flex-end;
              align-items: center;
              color: rgba(0, 0, 0, 0.5);

              .ninHelper {
                font-size: 12px;
              }

              .helpOne {
                display: flex;
                padding: 0 15px;
                align-items: center;
                font-size: 12px;

                &::before {
                  content: '';
                  display: block;
                  width: 12px;
                  height: 12px;
                  background: #00ace2;
                  margin-right: 5px;
                }
              }

              .helpTwo {
                display: flex;
                padding: 5px 15px 5px 5px;
                align-items: center;
                font-size: 12px;

                &::before {
                  content: '';
                  display: block;
                  width: 12px;
                  height: 12px;
                  background: #02be90;
                  margin-right: 5px;
                }
              }
            }

            .chartWrapper {
              display: flex;

              .axisTitle {
                color: rgba(0, 0, 0, 0.5);
                font-size: 12px;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                padding: 7px 0 42px;
                box-sizing: border-box;
                margin-right: -15px;

                &.effect {
                  padding: 52px 0 42px;
                }

                & > div {
                  word-break: keep-all;
                  white-space: nowrap;
                  text-align: right;
                }
              }
            }
          }
        }
      }
    }
  }

  @media print {
    body {
      background-image: none;
      background-color: white;

      #app {
        background-image: none;
      }

      .dashGround {
        background-image: none;
      }
    }

    #pulseReport {
      justify-content: flex-start;
      background-color: white;
      -webkit-print-color-adjust: exact !important;

      .page {
        break-inside: avoid !important;

        @page {
          size: a4;
        }
      }

      .wrapper {
        .reportCard {
          border: none;
          box-shadow: none;

          .content {
            .el-pagination {
              display: none;
            }

            #verbatimTable {
              border: 1px solid rgba(196, 196, 196, 0.5) !important;
            }
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  #pulseReport {
    .wrapper {
      .reportCard {
        .content {
          .el-pagination {
            .el-pager {
              li {
                color: #c4c4c4 !important;

                &.active {
                  color: #00ace2 !important;
                }
              }
            }

            .btn-prev,
            .btn-next {
              color: #00ace2 !important;

              &:disabled {
                color: #c4c4c4 !important;
              }
            }
          }
        }
      }
    }

    @media print {
      .el-card__body {
        padding: 0 70px !important;
      }
    }
  }
</style>
